import type { AcquireArchiveResult, AcquireResult, ArchiveAcquisitionConfig, ArchiveType, FileAnalysis, Person, PresenceDay, RunDetail, Scope } from '@bk/contracts';
import type { InformationSpec, ProcessContract } from '@bk/process-dsl';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, apiUpload, ApiRequestError, errorMessage } from '../../lib/api';
import { humanize, todayIso } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, Card, ErrorText, Select } from '../../ui';
import { FileDrop } from './FileDrop';
import { useSites } from './SiteScope';

type Row = Record<string, string>;

/**
 * Riempire l'elenco da un export: il file va in archivio; la prima volta si abbinano i campi alle
 * colonne, poi la procedura di acquisizione è appresa e i file con lo stesso layout si leggono da soli.
 */
function FromFile({ processId, spec, onRows }: { processId: string; spec: InformationSpec; onRows: (rows: Row[], documentId: string) => void }) {
  const { t } = useTenant();
  const [pending, setPending] = useState<Extract<AcquireResult, { needs_mapping: true }> & { documento_id: string } | null>(null);
  const [map, setMap] = useState<Record<string, string | null>>({});
  const [note, setNote] = useState('');
  const acquire = useMutation({
    mutationFn: async (input: { file?: File; documentId?: string; fieldMap?: Record<string, string | null> }) => {
      let documentId = input.documentId;
      if (input.file) {
        const analysis = await apiUpload<FileAnalysis>(t('/files'), input.file);
        if (analysis.tipo_file !== 'tabella') throw new Error('Il file non contiene una tabella: servono CSV o Excel');
        documentId = analysis.documento_id;
      }
      const res = await api<AcquireResult>(t(`/processes/${processId}/acquire`), { body: { info_key: spec.key, documento_id: documentId, ...(input.fieldMap ? { field_map: input.fieldMap } : {}) } });
      return { res, documentId: documentId! };
    },
    onSuccess: ({ res, documentId }) => {
      if (res.needs_mapping) {
        setPending({ ...res, documento_id: documentId });
        setMap(res.proposta);
        setNote('');
      } else {
        setPending(null);
        onRows(res.righe as Row[], documentId);
        setNote(`${res.righe.length} righe dal file${res.appresa ? ' (letto come le volte precedenti)' : ''}. Puoi correggerle qui sotto.`);
      }
    },
  });
  return (
    <div className="space-y-2">
      {!pending && <FileDrop onFile={(file) => acquire.mutate({ file })} disabled={acquire.isPending} label={acquire.isPending ? 'Lettura del file…' : 'Carica da file (export del gestionale)'} accept=".csv,.xlsx" />}
      {pending && (
        <div className="rounded-lg border border-black/10 p-3 text-sm">
          <p className="mb-2 text-ink/70">Prima volta per questo tipo di file ({pending.righe_totali} righe): da quale colonna prendo ogni dato?</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {pending.campi.map((f) => (
              <Select key={f.nome} label={`${humanize(f.nome)}${f.nome === spec.itemKey ? ' *' : ''}`} value={map[f.nome] ?? ''} onChange={(e) => setMap({ ...map, [f.nome]: e.target.value || null })}>
                <option value="">— nessuna —</option>
                {pending.colonne.map((c) => (
                  <option key={c.nome} value={c.nome}>
                    {c.nome}
                  </option>
                ))}
              </Select>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Button disabled={acquire.isPending || (!!spec.itemKey && !map[spec.itemKey])} onClick={() => acquire.mutate({ documentId: pending.documento_id, fieldMap: map })}>
              Usa queste colonne
            </Button>
            <Button variant="ghost" onClick={() => setPending(null)}>
              Annulla
            </Button>
          </div>
        </div>
      )}
      {note && <p className="text-xs text-emerald-700">{note}</p>}
      <ErrorText>{acquire.error ? errorMessage(acquire.error) : ''}</ErrorText>
    </div>
  );
}

/**
 * Riempire l'informazione da dati già in archivio (export caricati o programmi collegati). La prima
 * volta si indica da dove prenderla; poi la configurazione è ricordata per il processo.
 */
function FromArchive({ processId, spec, onList, onPresence }: {
  processId: string;
  spec: InformationSpec;
  onList: (rows: Row[], fonte: string) => void;
  onPresence: (present: Record<string, boolean>, fonte: string) => void;
}) {
  const { t } = useTenant();
  const types = useTenantQuery<{ data: ArchiveType[] }>(['archive', 'types'], '/archive/types');
  const [configuring, setConfiguring] = useState(false);
  const [tipo, setTipo] = useState('');
  const [map, setMap] = useState<Record<string, string | null>>({});
  const [pres, setPres] = useState({ attributo_persona: '', confronto: 'email' as 'email' | 'nome', attributo_dal: '', attributo_al: '' });
  const [note, setNote] = useState('');
  const ty = types.data?.data.find((x) => x.nome === tipo);
  const acquire = useMutation({
    mutationFn: (config?: ArchiveAcquisitionConfig) => api<AcquireArchiveResult>(t(`/processes/${processId}/acquire-archive`), { body: { info_key: spec.key, ...(config ? { config } : {}) } }),
    onSuccess: (res) => {
      setConfiguring(false);
      if (res.kind === 'lista') {
        onList(res.righe, res.fonte);
        setNote(`${res.righe.length} righe da ${res.config.tipo.replace(/_/g, ' ')}. Puoi correggerle qui sotto.`);
      } else {
        onPresence(Object.fromEntries(res.presenze.map((p) => [p.person_id, p.present])), res.fonte);
        const absent = res.presenze.filter((p) => !p.present).length;
        setNote(`${absent} assenti oggi secondo ${res.config.tipo.replace(/_/g, ' ')}${res.non_abbinate.length ? ` · non riconosciuti: ${res.non_abbinate.join(', ')}` : ''}. Puoi correggere a mano.`);
      }
    },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.body.code === 'no_acquisition_config') setConfiguring(true);
    },
  });
  if (!types.data?.data.length) return null;
  const config = (): ArchiveAcquisitionConfig =>
    spec.type === 'lista' ? { kind: 'lista', tipo, filtri: [], field_map: map } : { kind: 'presenze', tipo, ...pres };
  const ready = !!tipo && (spec.type === 'lista' ? !spec.itemKey || !!map[spec.itemKey] : !!pres.attributo_persona && !!pres.attributo_dal && !!pres.attributo_al);
  const attrs = ty?.attributi ?? [];
  const attrSelect = (label: string, value: string, set: (v: string) => void, kind?: string) => (
    <Select label={label} value={value} onChange={(e) => set(e.target.value)}>
      <option value="">— nessuno —</option>
      {attrs.filter((a) => !kind || a.tipo === kind).map((a) => <option key={a.nome} value={a.nome}>{humanize(a.nome)}</option>)}
    </Select>
  );
  return (
    <div className="space-y-2">
      {!configuring && (
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" disabled={acquire.isPending} onClick={() => acquire.mutate(undefined)}>
            {acquire.isPending ? 'Lettura…' : 'Prendi dall’archivio'}
          </Button>
          {note && <button className="text-xs text-ink/50 hover:text-ink" onClick={() => setConfiguring(true)}>cambia da dove</button>}
        </div>
      )}
      {configuring && (
        <div className="space-y-2 rounded-lg border border-black/10 p-3 text-sm">
          <p className="text-ink/70">{spec.type === 'presenze' ? 'Da quali dati si vedono le assenze?' : 'Da quali dati prendo l’elenco?'} Lo ricordo per le prossime volte.</p>
          <Select label="Dati dell’archivio" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="" />
            {types.data.data.map((x) => <option key={x.nome} value={x.nome}>{x.etichetta} ({x.entita})</option>)}
          </Select>
          {ty && spec.type === 'lista' && (
            <div className="grid gap-2 sm:grid-cols-2">
              {Object.keys(spec.fields).map((f) => attrSelect(`${humanize(f)}${f === spec.itemKey ? ' *' : ''}`, map[f] ?? '', (v) => setMap({ ...map, [f]: v || null })))}
            </div>
          )}
          {ty && spec.type === 'presenze' && (
            <div className="grid gap-2 sm:grid-cols-2">
              {attrSelect('Chi è assente', pres.attributo_persona, (v) => setPres({ ...pres, attributo_persona: v }))}
              <Select label="Riconosciuto per" value={pres.confronto} onChange={(e) => setPres({ ...pres, confronto: e.target.value as 'email' | 'nome' })}>
                <option value="email">email</option>
                <option value="nome">nome e cognome</option>
              </Select>
              {attrSelect('Assente dal', pres.attributo_dal, (v) => setPres({ ...pres, attributo_dal: v }), 'data')}
              {attrSelect('Assente al', pres.attributo_al, (v) => setPres({ ...pres, attributo_al: v }), 'data')}
            </div>
          )}
          <div className="flex gap-2">
            <Button disabled={!ready || acquire.isPending} onClick={() => acquire.mutate(config())}>Usa questi dati</Button>
            <Button variant="ghost" onClick={() => setConfiguring(false)}>Annulla</Button>
          </div>
        </div>
      )}
      {note && !configuring && <p className="text-xs text-emerald-700">{note}</p>}
      <ErrorText>{acquire.error && !(acquire.error instanceof ApiRequestError && acquire.error.body.code === 'no_acquisition_config') ? errorMessage(acquire.error) : ''}</ErrorText>
    </div>
  );
}

/** Elenco inserito a mano: righe modificabili, oppure incolla (una riga per elemento, campi separati da virgola o tab). */
function ListInput({ spec, rows, onChange }: { spec: InformationSpec; rows: Row[]; onChange: (r: Row[]) => void }) {
  const fields = Object.keys(spec.fields);
  const [paste, setPaste] = useState('');
  const set = (i: number, f: string, v: string) => onChange(rows.map((r, n) => (n === i ? { ...r, [f]: v } : r)));
  const importPaste = () => {
    const parsed = paste
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => Object.fromEntries(l.split(/\t|,|;/).map((v, i) => [fields[i], v.trim()]).filter(([k]) => k)) as Row);
    onChange([...rows.filter((r) => Object.values(r).some(Boolean)), ...parsed]);
    setPaste('');
  };
  return (
    <div className="space-y-2">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-ink/55">
            {fields.map((f) => (
              <th key={f} className="pb-1 font-medium">
                {humanize(f)}
                {f === spec.itemKey && ' *'}
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {fields.map((f) => (
                <td key={f} className="pr-2 pb-1">
                  <input className="w-full rounded border border-black/15 px-2 py-1" value={r[f] ?? ''} onChange={(e) => set(i, f, e.target.value)} type={spec.fields[f] === 'data' ? 'date' : spec.fields[f] === 'numero' ? 'number' : 'text'} />
                </td>
              ))}
              <td className="pb-1">
                <button className="px-2 text-ink/40 hover:text-red-600" onClick={() => onChange(rows.filter((_, n) => n !== i))} aria-label="Rimuovi riga">
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap gap-2">
        <Button variant="ghost" onClick={() => onChange([...rows, {}])}>
          + Aggiungi riga
        </Button>
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer text-ink/60">Incolla più righe</summary>
        <textarea className="mt-2 h-24 w-full rounded border border-black/15 p-2 font-mono text-xs" placeholder={`${fields.join(', ')}\n…`} value={paste} onChange={(e) => setPaste(e.target.value)} />
        <Button variant="ghost" disabled={!paste.trim()} onClick={importPaste}>
          Aggiungi le righe incollate
        </Button>
      </details>
    </div>
  );
}

/**
 * Avvio di un'esecuzione: il modulo nasce dal fabbisogno dichiarato nel contratto. Base sempre
 * manuale; come plus si può prendere da un file o dai dati già in archivio (export e programmi collegati).
 */
export function StartRun({ processId, contract, onCancel, scope }: { processId: string; contract: ProcessContract; onCancel: () => void; scope?: Scope }) {
  const { tenant, t } = useTenant();
  const people = useTenantQuery<{ data: Person[] }>(['people'], '/people');
  // Processo di più sedi (ADR 0017): si esegue in una sede, con le persone di quella sede.
  const { name: siteName } = useSites();
  const runSites = scope?.ambito === 'sedi' ? scope.sedi : [];
  const [siteId, setSiteId] = useState<string>(runSites.length === 1 ? runSites[0]! : '');
  const presence = useTenantQuery<PresenceDay>(['presence', todayIso()], `/presence?day=${todayIso()}`);
  const [values, setValues] = useState<Record<string, unknown>>(() => Object.fromEntries(contract.information.filter((i) => i.type === 'lista').map((i) => [i.key, [{}]])));
  const presenceSpec = contract.information.find((i) => i.type === 'presenze');
  const staff = (people.data?.data ?? []).filter(
    (p) => p.active && p.roles.some((r) => contract.roles.includes(r)) && (!siteId || p.all_sites || p.sites.some((x) => x.site_id === siteId)),
  );
  const [present, setPresent] = useState<Record<string, boolean>>({});
  const [provenance, setProvenance] = useState<Record<string, { documento_id?: string; fonte?: string }>>({});

  useEffect(() => {
    if (!presenceSpec || !people.data || !presence.data) return;
    const known = new Map(presence.data.data.map((p) => [p.person_id, p.present]));
    setPresent(Object.fromEntries(staff.map((p) => [p.id, known.get(p.id) ?? true])));
  }, [people.data, presence.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useMutation({
    mutationFn: () => {
      const information: Record<string, unknown> = {};
      for (const spec of contract.information) {
        if (spec.type === 'presenze') information[spec.key] = staff.map((p) => ({ person_id: p.id, present: present[p.id] ?? false }));
        else if (spec.type === 'lista') information[spec.key] = ((values[spec.key] as Row[]) ?? []).filter((r) => Object.values(r).some((v) => v?.trim()));
        else if (values[spec.key] !== undefined && values[spec.key] !== '') information[spec.key] = values[spec.key];
      }
      return api<RunDetail>(t(`/processes/${processId}/runs`), { body: { information, provenance, ...(siteId ? { site_id: siteId } : {}) } });
    },
    onSuccess: (run) => navigate(`/t/${tenant.id}/esecuzioni/${run.id}`),
  });
  const issues = start.error instanceof ApiRequestError && Array.isArray(start.error.body.details) ? (start.error.body.details as Array<{ path: string; message: string }>) : [];

  return (
    <Card>
      <h2 className="mb-1 text-base font-semibold">Avvia {contract.name}</h2>
      <p className="mb-5 text-sm text-ink/60">Inserisci le informazioni che il processo richiede: a mano, da un file o dai dati in archivio.</p>
      {runSites.length > 1 && (
        <div className="mb-5 max-w-xs">
          <Select label="In quale sede" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">scegli la sede</option>
            {runSites.map((id) => (
              <option key={id} value={id}>
                {siteName(id)}
              </option>
            ))}
          </Select>
        </div>
      )}
      <div className="space-y-6">
        {contract.information.map((spec) => (
          <div key={spec.key}>
            <p className="mb-2 text-sm font-medium">
              {spec.description ?? humanize(spec.key)}
              {spec.required ? '' : ' (facoltativa)'}
            </p>
            {spec.type === 'lista' && (
              <div className="space-y-3">
                <FromFile
                  processId={processId}
                  spec={spec}
                  onRows={(rows, documentId) => {
                    setValues({ ...values, [spec.key]: rows });
                    setProvenance({ ...provenance, [spec.key]: { documento_id: documentId } });
                  }}
                />
                <FromArchive
                  processId={processId}
                  spec={spec}
                  onList={(rows, fonte) => {
                    setValues({ ...values, [spec.key]: rows });
                    setProvenance({ ...provenance, [spec.key]: { fonte } });
                  }}
                  onPresence={() => undefined}
                />
                <ListInput spec={spec} rows={(values[spec.key] as Row[]) ?? []} onChange={(rows) => setValues({ ...values, [spec.key]: rows })} />
              </div>
            )}
            {spec.type === 'presenze' && (
              <FromArchive
                processId={processId}
                spec={spec}
                onList={() => undefined}
                onPresence={(p, fonte) => {
                  setPresent({ ...present, ...p });
                  setProvenance({ ...provenance, [spec.key]: { fonte } });
                }}
              />
            )}
            {spec.type === 'presenze' && (
              <div className="mt-2 grid gap-1 sm:grid-cols-2">
                {staff.length === 0 && <p className="text-sm text-amber-700">Nessuna persona con i ruoli del processo: aggiungile in “Persone”.</p>}
                {staff.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={present[p.id] ?? false} onChange={(e) => setPresent({ ...present, [p.id]: e.target.checked })} />
                    {p.name} <span className="text-ink/45">({p.roles.filter((r) => contract.roles.includes(r)).map(humanize).join(', ')})</span>
                  </label>
                ))}
              </div>
            )}
            {(spec.type === 'testo' || spec.type === 'documento') && (
              <textarea
                className="h-20 w-full rounded-lg border border-black/15 p-2 text-sm"
                placeholder={spec.type === 'documento' ? 'Riferimento al documento (il caricamento dei file arriva con l’archivio)' : ''}
                value={(values[spec.key] as string) ?? ''}
                onChange={(e) => setValues({ ...values, [spec.key]: e.target.value })}
              />
            )}
            {(spec.type === 'numero' || spec.type === 'data') && (
              <input
                className="rounded-lg border border-black/15 px-3 py-2 text-sm"
                type={spec.type === 'data' ? 'date' : 'number'}
                value={(values[spec.key] as string) ?? ''}
                onChange={(e) => setValues({ ...values, [spec.key]: e.target.value })}
              />
            )}
            {issues.filter((i) => i.path === spec.key || i.path.startsWith(`${spec.key}.`)).map((i) => (
              <p key={i.path} className="mt-1 text-xs text-red-700">
                {i.path !== spec.key && <code className="mr-1">{i.path.slice(spec.key.length + 1)}</code>}
                {i.message}
              </p>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-6 flex gap-2">
        <Button disabled={start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? 'Avvio…' : 'Avvia'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Annulla
        </Button>
      </div>
      <div className="mt-3">
        <ErrorText>{start.error && !issues.length ? errorMessage(start.error) : ''}</ErrorText>
      </div>
    </Card>
  );
}
